import { forwardRef } from 'react';
import { Link as RouterLink } from 'react-router';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { ArrowUpRight } from '@/icons';

type Variant = 'primary' | 'secondary' | 'ghost';

type CommonProps = {
  variant?: Variant;
  children: ReactNode;
  className?: string;
  withArrow?: boolean;
};

type ButtonAsAnchor = CommonProps &
  Omit<ComponentPropsWithoutRef<'a'>, 'href'> & {
    href: string;
    to?: never;
    type?: never;
  };

type ButtonAsRouterLink = CommonProps &
  Omit<ComponentPropsWithoutRef<typeof RouterLink>, 'to'> & {
    to: string;
    href?: never;
    type?: never;
  };

type ButtonAsButton = CommonProps &
  ComponentPropsWithoutRef<'button'> & {
    to?: never;
    href?: never;
  };

type ButtonProps = ButtonAsButton | ButtonAsAnchor | ButtonAsRouterLink;

function getClasses(variant: Variant, extra?: string) {
  const base =
    'inline-flex items-center gap-2 font-sans font-semibold leading-none transition-all duration-200 cursor-pointer select-none focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';
  const styles: Record<Variant, string> = {
    primary:
      'bg-accent text-bg px-7 py-4 rounded-full hover:bg-accent-hover hover:scale-[1.02] active:scale-[0.98]',
    secondary:
      'border border-ink-primary text-ink-primary px-7 py-4 hover:bg-ink-primary hover:text-bg',
    ghost:
      'text-ink-primary hover:text-accent underline-offset-4 hover:underline',
  };
  return cn(base, styles[variant], extra);
}

export const Button = forwardRef<HTMLElement, ButtonProps>(function Button(props, ref) {
  const { variant = 'primary', children, className, withArrow, ...rest } = props;
  const classes = getClasses(variant, className);
  const content = (
    <>
      <span>{children}</span>
      {withArrow && <ArrowUpRight className="w-4 h-4" />}
    </>
  );

  if ('to' in rest && rest.to !== undefined) {
    const { to, ...linkRest } = rest;
    return (
      <RouterLink
        ref={ref as React.Ref<HTMLAnchorElement>}
        to={to}
        className={classes}
        {...linkRest}
      >
        {content}
      </RouterLink>
    );
  }

  if ('href' in rest && rest.href !== undefined) {
    const { href, ...anchorRest } = rest;
    return (
      <a
        ref={ref as React.Ref<HTMLAnchorElement>}
        href={href}
        className={classes}
        {...anchorRest}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      ref={ref as React.Ref<HTMLButtonElement>}
      className={classes}
      {...(rest as ComponentPropsWithoutRef<'button'>)}
    >
      {content}
    </button>
  );
});
